import React, { useEffect, useRef, useState } from "react";
import {
  Row,
  Col,
  Form,
  Button,
  Modal,
  Card as BootstrapCard,
  Popover,
  Overlay,
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ErrorMessage, Formik } from "formik";
import * as Yup from "yup";
import { jwtDecode } from "jwt-decode";
import Card from "../../../components/Card";
import url from "../../../configs/config";
import FocusError from "../../../components/error/error-focus";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import InputField from "../../../components/input/input-field";
import FadeLoaderComponent from "../../../components/loader-component";
import { roleTypes } from "../../../data/roleType";
import * as SettingSelector from "../../../store/setting/selectors";
import { useSelector } from "react-redux";

const RackEdit = () => {
  const { id } = useParams();
  const getRackRef = useRef(false);
  const getBinRef = useRef(false);
  const alertRefEdit = useRef(null);
  const alertRefBin = useRef(null);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [decodedToken, setDecodedToken] = useState();
  const token = localStorage.getItem("coyote-token");
  const [rackLoading, setRackLoading] = useState(true);
  const [isData, setIsData] = useState(false);
  const permissions = useSelector(SettingSelector.set_permissions);
  const rackManagementHasUpdatePermission =
    permissions?.rack_management?.update === true;
  const rackManagementHasUpdateBinStatusPermission =
    permissions?.rack_management?.update_bin_status === true;
  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });
  const [formData, setFormData] = useState({
    name: "",
    rackNumber: "",
    description: "",
    warehouseName: "",
    totalRows: 1,
    totalColumns: 1,
  });

  const getRack = async () => {
    if (getRackRef.current === true) return;
    getRackRef.current = true;

    try {
      if (!token) {
        localStorage.removeItem("coyote-token");

        navigate(
          `/${
            decodedToken?.roles?.some(
              (role) => role.role_type === roleTypes.SUPER_ADMIN
            )
              ? "admin"
              : "user"
          }/auth/sign-in`,
          {
            state: { status: "invalid-token" },
          }
        );
        return;
      }
      setRackLoading(true);
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };
      const response = await axios.get(`${url.backendURL}rack/${id}`, config);
      const data = response.data.response.data;
      setFormData({
        name: data.rackName,
        rackNumber: data.rackNumber,
        description: data.description,
        warehouseName: data.warehouseName,
        totalRows: data.totalRows,
        totalColumns: data.totalColumns,
      });
      setIsData(true);
    } catch (err) {
      handleError(err, "rack-edit");
    } finally {
      getRackRef.current = false;
      setRackLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      try {
        if (!id) {
          navigate("/dashboard/app/rack-list");
        }
        const decoded = jwtDecode(token);
        const user = decoded?.user;
        setDecodedToken(user);
        getRack();
        if (
          rackManagementHasUpdateBinStatusPermission ||
          user?.roles?.some((role) => role.role_type === roleTypes.SUPER_ADMIN)
        ) {
          getWarehouseRackBins();
        }
      } catch (error) {
        console.error("Failed to decode token", error);
      }
    }
  }, [token]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    setErrors({});
    try {
      if (!token) {
        localStorage.removeItem("coyote-token");

        navigate(
          `/${
            decodedToken?.roles?.some(
              (role) => role.role_type === roleTypes.SUPER_ADMIN
            )
              ? "admin"
              : "user"
          }/auth/sign-in`,
          {
            state: { status: "invalid-token" },
          }
        );
        return;
      }
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };
      const data = {
        name: values.name,
        rackNumber: values.rackNumber,
        description: values.description,
      };

      const response = await axios.put(
        `${url.backendURL}rack/${id}`,
        data,
        config
      );

      setFormData({
        ...formData,
        name: values.name,
        rackNumber: values.rackNumber,
        description: values.description,
      });

      resetForm({
        values: {
          name: values.name,
          rackNumber: values.rackNumber,
          description: values.description,
          warehouseName: values.warehouseName,
          totalRows: values.totalRows,
          totalColumns: values.totalColumns,
        },
      });

      if (alertRefEdit?.current) {
        alertRefEdit.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      setAlert({
        message: response && response.data.message,
        type: "success",
        show: true,
        alertFor: "edit-rack",
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 2000);
    } catch (err) {
      if (alertRefEdit?.current) {
        alertRefEdit.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      handleError(err, "edit-rack");
    } finally {
      setSubmitting(false);
    }
  };

  const handleError = (err, alertFor) => {
    const { response } = err;
    if (alertFor === "edit-rack") {
      if (alertRefEdit.current) {
        alertRefEdit.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    } else {
      if (alertRefBin.current) {
        alertRefBin.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }

    if (
      response?.data?.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        }
      );
    } else {
      const responseErrors = response?.data?.error || {};
      setErrors(responseErrors);
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .matches(
        /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
        "Rack name must contain at least one letter or number"
      )
      .min(1, "Rack name must be at least 1 character long")
      .max(50, "Rack name must not exceed 50 characters")
      .trim()
      .required("Rack Name is required"),
    rackNumber: Yup.string()
      .min(1, "Rack Number must be at least 1 character long")
      .max(20, "Rack Number must not exceed 20 characters")
      .trim()
      .required("Rack Number is required"),
    description: Yup.string()
      .trim()
      .optional()
      .min(2, "Description must be greater than 2 characters")
      .matches(/[a-zA-Z]/, "Description must contain at least one letter"),
  });

  const handleReset = (resetForm) => {
    resetForm({
      values: {
        name: formData.name,
        rackNumber: formData.rackNumber,
        description: formData.description,
        warehouseName: formData.warehouseName,
        totalRows: formData.totalRows,
        totalColumns: formData.totalColumns,
      },
    });
    setErrors({});
  };

  const alertRef = useRef(null);
  const dropdownRef = useRef(null);
  const gridRef = React.useRef(null);
  const [bins, setBins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaderId, setLoaderId] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [showPopover, setShowPopover] = useState(null);
  const triggersRef = useRef({ currentOpenId: null, triggers: {} });
  const savedScrollRef = React.useRef({ top: 0, left: 0 });

  const handleClose = () => {
    setShowPopover(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !event.target.closest(".edit-icon")
      ) {
        setDropdownOpen(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (showPopover) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showPopover]);

  const handleStatusChange = async (status, binId) => {
    await callStatusUpdateAPI(status, binId);
    setDropdownOpen(null);
  };

  const toggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
  };

  const getWarehouseRackBins = async (loaderId = null) => {
    if (getBinRef.current === true) return;
    getBinRef.current = true;
    if (loaderId) {
      setLoaderId(loaderId);
    } else {
      setLoading(true);
    }
    if (!token) {
      localStorage.removeItem("coyote-token");
      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "invalid-token" },
        }
      );
      setRackBinLoader(false);
      return;
    }
    const config = {
      headers: {
        Authorization: `${token}`,
      },
    };

    try {
      const response = await axios.get(
        `${url.backendURL}rack/${id}/bins`,
        config
      );
      const data = response?.data?.response?.data;
      setBins(data);
    } catch (err) {
      if (alertRef?.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      handleError(err, "edit-rack");
    } finally {
      getBinRef.current = false;
      setLoaderId(null);
      setLoading(false);
    }
  };

  const generateRackBinLayout = (rackBins = [], totalRows, totalColumns) => {
    const layout = [];

    for (let row = totalRows; row >= 1; row--) {
      const rowRackBins = [];
      for (let col = 1; col <= totalColumns; col++) {
        const existingRackBin =
          rackBins &&
          rackBins?.length > 0 &&
          rackBins?.find(
            (bin) => bin.rowNumber === row && bin.columnNumber === col
          );

        rowRackBins.push({
          id: existingRackBin?.id || null,
          rowNumber: row,
          columnNumber: col,
          status: existingRackBin?.status || "active",
          productId: existingRackBin?.productId || null,
          productName: existingRackBin?.productName || null,
          quantity: existingRackBin?.quantity || null,
        });
      }
      layout.push(rowRackBins);
    }

    return layout;
  };

  const callStatusUpdateAPI = async (status, binId) => {
    setIsSubmitting(true);
    try {
      if (!token) {
        localStorage.removeItem("coyote-token");

        navigate(
          `/${
            decodedToken?.roles?.some(
              (role) => role.role_type === roleTypes.SUPER_ADMIN
            )
              ? "admin"
              : "user"
          }/auth/sign-in`,
          {
            state: { status: "invalid-token" },
          }
        );
        return;
      }
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };
      const data = {
        status,
      };

      const response = await axios.put(
        `${url.backendURL}rack/${id}/bin/${binId}/status`,
        data,
        config
      );

      if (alertRefBin?.current) {
        alertRefBin.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      setAlert({
        message: response && response.data.message,
        type: "success",
        show: true,
        alertFor: "update-bin-status",
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 2000);
      if (
        rackManagementHasUpdateBinStatusPermission ||
        decodedToken?.roles?.some(
          (role) => role.role_type === roleTypes.SUPER_ADMIN
        )
      ) {
        await getWarehouseRackBins(binId);
      }
    } catch (err) {
      if (alertRefBin?.current) {
        alertRefBin.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      handleError(err, "update-bin-status");
    } finally {
      setIsSubmitting(false);
      triggersRef.current.currentOpenId = null;
    }
  };

  const Dropdown = ({ binId, hasProduct, status }) => {
    const inactiveId = `inactive-${binId}`;
    const damageId = `damage-${binId}`;

    const setInactiveRef = React.useCallback(
      (el) => {
        if (el) triggersRef.current.triggers[inactiveId] = el;
      },
      [inactiveId]
    );

    const setDamageRef = React.useCallback(
      (el) => {
        if (el) triggersRef.current.triggers[damageId] = el;
      },
      [damageId]
    );

    return (
      <div
        className="h-100 w-75 status-dropdown position-absolute bg-white border rounded shadow"
        style={{ right: 0, top: 0, zIndex: 90 }}
      >
        <div
          className="h-100 d-flex flex-column justify-content-evenly"
          ref={dropdownRef}
        >
          <div
            className="dropdown-item text-success text-center w-100 py-2"
            onClick={(e) => {
              e.stopPropagation();
              if (status !== "active") {
                handleStatusChange("active", binId);
                setDropdownOpen(null);
                setShowPopover(null);
              }
            }}
            role="button"
            tabIndex={0}
            style={{
              cursor: status === "active" && "not-allowed",
            }}
          >
            Active
          </div>

          <button
            className="dropdown-item text-dark text-center w-100 py-2"
            ref={setInactiveRef}
            onClick={(e) => {
              e.stopPropagation();
              if (status !== "in-active") {
                if (hasProduct) {
                  setShowPopover((prev) =>
                    prev === inactiveId ? null : inactiveId
                  );
                } else {
                  setShowPopover((prev) =>
                    prev === inactiveId ? null : inactiveId
                  );
                }
              }
            }}
            style={{
              cursor: status === "in-active" && "not-allowed",
            }}
            type="button"
          >
            In-active
          </button>

          <Overlay
            target={() => triggersRef.current.triggers[inactiveId]}
            show={showPopover === inactiveId}
            placement="top"
            container={document.body}
            onHide={() => setShowPopover(null)}
          >
            {({
              placement,
              arrowProps,
              show: _show,
              popper,
              ...overlayProps
            }) => {
              return (
                <Popover
                  id={`popover-inactive-${binId}`}
                  style={{ zIndex: 9999, minWidth: 220 }}
                  {...overlayProps}
                >
                  <Popover.Header
                    as="h2"
                    className="bg-white modal-title d-flex justify-content-between align-items-center custom-modal-header-rack "
                  >
                    {hasProduct ? "Cannot Change Status" : "Confirm Status"}
                    {hasProduct && (
                      <button
                        type="button"
                        className="btn-close-svg"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowPopover(null);
                        }}
                        aria-label="Close"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                        >
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                        </svg>
                      </button>
                    )}
                  </Popover.Header>
                  <Popover.Body className="custom-modal-body text-sm">
                    {hasProduct ? (
                      <div>
                        To set this bin to in-active,{" "}
                        <strong>first move the inside product</strong> to
                        another bin.
                      </div>
                    ) : (
                      <>
                        Are you sure you want to mark this as{" "}
                        <strong>In-active</strong>?
                        <div className="d-flex justify-content-end mt-2">
                          <Button
                            className="btn btn-primary me-1"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusChange("in-active", binId);
                              setShowPopover(null);
                            }}
                            disabled={isSubmitting}
                          >
                            Confirm
                          </Button>
                          <Button
                            type="button"
                            variant="btn btn-danger"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowPopover(null);
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </>
                    )}
                  </Popover.Body>
                </Popover>
              );
            }}
          </Overlay>

          <button
            className="dropdown-item text-danger text-center w-100 py-2"
            ref={setDamageRef}
            style={{
              cursor: status === "damaged" && "not-allowed",
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (status !== "damaged") {
                if (hasProduct) {
                  setShowPopover((prev) =>
                    prev === damageId ? null : damageId
                  );
                } else {
                  setShowPopover((prev) =>
                    prev === damageId ? null : damageId
                  );
                }
              }
            }}
            type="button"
          >
            Damaged
          </button>

          <Overlay
            target={() => triggersRef.current.triggers[damageId]}
            show={showPopover === damageId}
            placement="top"
            container={document.body}
            onHide={() => setShowPopover(null)}
          >
            {({
              placement,
              arrowProps,
              show: _show,
              popper,
              ...overlayProps
            }) => {
              return (
                <Popover
                  id={`popover-damaged-${binId}`}
                  style={{ zIndex: 9999, minWidth: 220 }}
                  {...overlayProps}
                >
                  <Popover.Header
                    as="h2"
                    className="bg-white modal-title d-flex justify-content-between align-items-center custom-modal-header-rack "
                  >
                    {hasProduct ? "Cannot Change Status" : "Confirm Status"}
                    {hasProduct && (
                      <button
                        type="button"
                        className="btn-close-svg"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowPopover(null);
                        }}
                        aria-label="Close"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                        >
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                        </svg>
                      </button>
                    )}
                  </Popover.Header>
                  <Popover.Body className="custom-modal-body text-sm">
                    {hasProduct ? (
                      <div>
                        To mark this bin as damaged,{" "}
                        <strong>first move the inside product</strong> to
                        another bin.
                      </div>
                    ) : (
                      <>
                        Are you sure you want to mark this as{" "}
                        <strong>Damaged</strong>?
                        <div className="d-flex justify-content-end mt-2">
                          <Button
                            className="btn btn-primary me-1"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusChange("damaged", binId);
                              setShowPopover(null);
                            }}
                            disabled={isSubmitting}
                          >
                            Confirm
                          </Button>
                          <Button
                            type="button"
                            variant="btn btn-danger"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowPopover(null);
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </>
                    )}
                  </Popover.Body>
                </Popover>
              );
            }}
          </Overlay>
        </div>
      </div>
    );
  };

  const BinsView = () => {
    const rememberScroll = React.useCallback(() => {
      const el = gridRef.current;

      if (el) {
        savedScrollRef.current = {
          top: el.scrollTop,
          left: el.scrollLeft,
        };
      }
    }, []);

    const restoreScroll = React.useCallback(() => {
      const el = gridRef.current;
      if (el) {
        el.scrollTop = savedScrollRef.current.top;
        el.scrollLeft = savedScrollRef.current.left;
      }
    }, []);

    React.useLayoutEffect(() => {
      restoreScroll();
    }, [restoreScroll, dropdownOpen]);

    return (
      <div style={{ minHeight: "400px" }}>
        <Form className="form-horizontal px-3">
          <Form.Group className="form-group mb-2">
            <div className="p-3 rounded-2">
              <p className="text-muted small mb-3">
                Click on any bin to update its status.
              </p>
              <div className="d-flex align-items-center gap-4 mb-3 flex-wrap">
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-1 border border-success border-2 me-2"
                    style={{ width: "16px", height: "16px" }}
                  ></div>
                  <span className="small text-dark">Active</span>
                </div>
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-1 border border-2 border-dark me-2"
                    style={{ width: "16px", height: "16px" }}
                  ></div>
                  <span className="small text-dark">Inactive</span>
                </div>
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-1 border border-2 border-danger me-2"
                    style={{ width: "16px", height: "16px" }}
                  ></div>
                  <span className="small text-dark">Damage</span>
                </div>
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-1 border border-2 border-success position-active position-fill me-2"
                    style={{ width: "16px", height: "16px" }}
                  ></div>
                  <span className="small text-dark">Fill</span>
                </div>
              </div>
              <div className="grid-container" ref={gridRef}>
                {loading && (
                  <div className="d-flex align-items-center justify-content-center">
                    <FadeLoaderComponent />
                  </div>
                )}
                {!loading && bins && (
                  <div
                    className="d-grid gap-3"
                    style={{
                      gridTemplateColumns: `repeat(${
                        formData?.totalColumns || 1
                      }, minmax(120px, 1fr))`,
                      justifyContent: "start",
                    }}
                  >
                    <div className="d-flex flex-column gap-3">
                      {generateRackBinLayout(
                        bins,
                        formData.totalRows,
                        formData.totalColumns
                      )?.map((rowRackBins, rowIndex) => (
                        <div
                          key={rowIndex}
                          className="d-flex gap-2 justify-content-start"
                        >
                          {rowRackBins?.map((rackBin, colIndex) => (
                            <div
                              key={`${rowIndex}-${colIndex}`}
                              className={`
                                bin-manage d-flex align-items-center justify-content-center flex-column position-relative
                                ${
                                  {
                                    active: "position-active",
                                    "in-active": "bin-position-inactive",
                                    damaged: "bin-position-damaged",
                                  }[rackBin.status] || ""
                                }
                                ${rackBin.productName ? "position-fill" : ""}
                              `}
                            >
                              {loaderId === rackBin.id ? (
                                <FadeLoaderComponent />
                              ) : (
                                <>
                                  <div className="position-absolute text-primary top-0 end-0 edit-icon-container z-n1">
                                    <div className="dropdown-wrapper position-relative">
                                      <Link
                                        className="edit-icon text-primary"
                                        title="Edit"
                                        onClick={(e) => e.preventDefault()}
                                      >
                                        <span
                                          className="btn-inner"
                                          onClick={(e) => {
                                            rememberScroll();
                                            setDropdownOpen(rackBin.id);
                                            requestAnimationFrame(
                                              restoreScroll
                                            );
                                          }}
                                        >
                                          <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                              d="M11.4925 2.78906H7.75349C4.67849 2.78906 2.75049 4.96606 2.75049 8.04806V16.3621C2.75049 19.4441 4.66949 21.6211 7.75349 21.6211H16.5775C19.6625 21.6211 21.5815 19.4441 21.5815 16.3621V12.3341"
                                              stroke="currentColor"
                                              strokeWidth="1.5"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            ></path>
                                            <path
                                              fillRule="evenodd"
                                              clipRule="evenodd"
                                              d="M8.82812 10.921L16.3011 3.44799C17.2321 2.51799 18.7411 2.51799 19.6721 3.44799L20.8891 4.66499C21.8201 5.59599 21.8201 7.10599 20.8891 8.03599L13.3801 15.545C12.9731 15.952 12.4211 16.181 11.8451 16.181H8.09912L8.19312 12.401C8.20712 11.845 8.43412 11.315 8.82812 10.921Z"
                                              stroke="currentColor"
                                              strokeWidth="1.5"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            ></path>
                                            <path
                                              d="M15.1655 4.60254L19.7315 9.16854"
                                              stroke="currentColor"
                                              strokeWidth="1.5"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            ></path>
                                          </svg>
                                        </span>
                                      </Link>
                                    </div>
                                  </div>

                                  {dropdownOpen &&
                                    dropdownOpen === rackBin.id && (
                                      <Dropdown
                                        binId={rackBin.id}
                                        hasProduct={!!rackBin.productId}
                                        status={rackBin.status}
                                      />
                                    )}

                                  <div className="box-content-container gap-1">
                                    <div className="">
                                      <span className="small fw-bold text-dark">
                                        Row{rackBin.rowNumber} Col
                                        {rackBin.columnNumber}
                                      </span>
                                    </div>

                                    <div className="name-container d-flex flex-column">
                                      <span
                                        className={`product-name ${
                                          rackBin.status === "damaged"
                                            ? "text-danger fw-bold"
                                            : rackBin.status === "in-active"
                                            ? "text-muted fw-bold"
                                            : ""
                                        }`}
                                      >
                                        {rackBin.productName
                                          ? rackBin.productName
                                          : rackBin.status === "in-active"
                                          ? "In-active bin"
                                          : rackBin.status === "damaged"
                                          ? "Damaged bin"
                                          : "No Product"}
                                      </span>
                                      {rackBin.quantity && (
                                        <div className="border-success">
                                          <span className="quantity-text">
                                            Qty: {rackBin.quantity}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    <div className=" mt-0 pt-0"></div>
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Form.Group>
        </Form>
      </div>
    );
  };

  return (
    <>
      {showPopover && (
        <div
          onClick={handleClose}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            overflow: "hidden",
            zIndex: 1000,
          }}
        />
      )}
      {(rackManagementHasUpdatePermission ||
        decodedToken?.roles?.some(
          (role) => role.role_type === roleTypes.SUPER_ADMIN
        )) && (
        <Formik
          initialValues={{
            name: formData?.name,
            rackNumber: formData?.rackNumber,
            description: formData?.description,
            warehouseName: formData?.warehouseName,
            totalRows: formData?.totalRows,
            totalColumns: formData?.totalColumns,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            resetForm,
          }) => (
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <FocusError />
              <Row>
                <Col xl="12" lg="12" ref={alertRefEdit}>
                  <Card>
                    <Card.Header className="d-flex justify-content-between">
                      <div
                        className="breadcrumb header-title"
                        style={{ cursor: "pointer" }}
                      >
                        <h4
                          onClick={() => navigate("/dashboard/app/rack-list")}
                          className="text-primary card-title"
                          style={{ cursor: "pointer" }}
                        >
                          <span>Rack</span>
                        </h4>
                        <h4 className="mx-2 card-title">{">"}</h4>
                        <h4 className="card-title">Edit</h4>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      {alert.alertFor === "edit-rack" && (
                        <AlertNotificationSimple
                          type={alert.type}
                          message={alert.message}
                          show={alert.show}
                          onClose={() => setAlert({ ...alert, show: false })}
                        />
                      )}
                      {isData && !rackLoading && (
                        <div className="new-rack-info">
                          <Row>
                            <InputField
                              className="col-md-6 form-group"
                              type="text"
                              name="name"
                              placeholder="Enter name"
                              value={values.name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              asterick={true}
                              errorMessage={errors?.name}
                              label="Name"
                            />
                            <InputField
                              className="col-md-6 form-group"
                              type="text"
                              name="rackNumber"
                              placeholder="Enter rack number"
                              value={values.rackNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              asterick={true}
                              errorMessage={errors?.rackNumber}
                              label="Rack Number"
                            />
                          </Row>
                          <Row>
                            <Col sm="6">
                              <Form.Group className="form-group">
                                <Form.Label
                                  htmlFor="totalRows"
                                  column
                                  sm="4"
                                  className="control-label align-self-center mb-0"
                                >
                                  Rows
                                </Form.Label>

                                <Form.Control
                                  type="number"
                                  name="totalRows"
                                  value={values.totalRows}
                                  disabled
                                  style={{ background: "transparent", cursor:"no-drop"}}
                                />
                              </Form.Group>
                            </Col>

                            <Col sm="6">
                              <Form.Group className="form-group">
                                <Form.Label
                                  htmlFor="totalColumns"
                                  column
                                  sm="4"
                                  className="control-label align-self-center mb-0"
                                >
                                  Columns
                                </Form.Label>

                                <Form.Control
                                  type="number"
                                  name="totalColumns"
                                  value={values?.totalColumns}
                                  disabled
                                  style={{ background: "transparent", cursor:"no-drop"}}
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row>
                            <Col sm="6">
                              <Form.Group className="form-group">
                                <Form.Label
                                  htmlFor="warehouseName"
                                  colum
                                  sm="4"
                                  className="control-label align-self-center"
                                >
                                  Warehouse
                                </Form.Label>

                                <Form.Control
                                  type="text"
                                  name="warehouseName"
                                  value={values?.warehouseName}
                                  disabled
                                  style={{ background: "transparent", cursor:"no-drop"}}
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <InputField
                            className="col-md-10 form-group"
                            as="textarea"
                            name="description"
                            placeholder="Enter description"
                            value={values.description}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            errorMessage={errors?.description}
                            label="Description"
                          />

                          <hr />
                          <div className="d-flex flex-wrap flex-md-nowrap justify-content-end gap-1 gap-md-2">
                            <Button
                              type="button"
                              className="me-2 me-md-5 px-3 px-md-4"
                              variant="btn btn-light"
                              onClick={() => handleReset(resetForm)}
                            >
                              Reset
                            </Button>

                            <Button
                              type="submit"
                              disabled={isSubmitting}
                              variant="btn btn-primary"
                              className="me-2  me-md-5 px-3 px-md-4"
                            >
                              Submit
                            </Button>
                            <Button
                              type="button"
                              className="px-3 px-md-4"
                              variant="btn btn-danger"
                              onClick={() =>
                                navigate("/dashboard/app/rack-list")
                              }
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                      {rackLoading && <FadeLoaderComponent />}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      )}

      {isData &&
        !loading &&
        (rackManagementHasUpdateBinStatusPermission ||
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN
          )) && (
          <Row>
            <Col sm={12} ref={alertRefBin}>
              <Card>
                <Card.Header className="d-flex justify-content-between">
                  <div
                    className="breadcrumb header-title"
                    style={{ cursor: "pointer" }}
                  >
                    <h4
                      onClick={() => navigate("/dashboard/app/rack-list")}
                      className="text-primary card-title"
                      style={{ cursor: "pointer" }}
                    >
                      <span>Rack</span>
                    </h4>
                    <h4 className="mx-2 card-title">{">"}</h4>
                    <h4 className="card-title">Bin Management</h4>
                  </div>
                  <div>
                    {isCollapsed ? (
                      <div
                        onClick={toggleCollapse}
                        className="sidebar-toggle"
                        style={{
                          position: "initial",
                          marginRight: "1.5rem",
                          cursor: "pointer",
                        }}
                      >
                        <i
                          className="icon"
                          style={{ transform: "rotate(270deg)" }}
                        >
                          <svg
                            width="20"
                            className="icon-20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M4.25 12.2744L19.25 12.2744"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            ></path>
                            <path
                              d="M10.2998 18.2988L4.2498 12.2748L10.2998 6.24976"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            ></path>
                          </svg>
                        </i>
                      </div>
                    ) : (
                      <div
                        className="sidebar-toggle"
                        onClick={toggleCollapse}
                        style={{
                          position: "initial",
                          marginRight: "1.5rem",
                          cursor: "pointer",
                        }}
                      >
                        <i
                          className="icon"
                          style={{ transform: "rotate(90deg)" }}
                        >
                          <svg
                            width="20"
                            className="icon-20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M4.25 12.2744L19.25 12.2744"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            ></path>
                            <path
                              d="M10.2998 18.2988L4.2498 12.2748L10.2998 6.24976"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            ></path>
                          </svg>
                        </i>
                      </div>
                    )}
                  </div>
                </Card.Header>
                {!isCollapsed && (
                  <Card.Body>
                    {alert.alertFor === "update-bin-status" && (
                      <AlertNotificationSimple
                        type={alert.type}
                        message={alert.message}
                        show={alert.show}
                        onClose={() => setAlert({ ...alert, show: false })}
                      />
                    )}
                    <div className="rounded">
                      <BinsView />
                    </div>
                  </Card.Body>
                )}
              </Card>
            </Col>
          </Row>
        )}
    </>
  );
};

export default RackEdit;
